import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import { Children, Fragment, isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { httpClient } from '../../common/httpclient';

/**
 * Personalização de indicadores dos dashboards (desktop). Ref: `ui-ux/prototipo-dashboard-personalizar.html`.
 *
 * - Board de 12 colunas x linhas de 20px (gap 12): cada indicador tem largura em colunas (`w`) e altura
 *   opcional em linhas (`h`; vazio = altura do conteúdo). Ordem, tamanho e visibilidade valem também fora da edição.
 * - Modo edição: arrastar o card = mover, alça de canto/borda = redimensionar, botão ocultar, bandeja
 *   "Ocultos", Ocultar todos, Restaurar padrão, Cancelar e Salvar. Nada é gravado até Salvar.
 * - Teclado (card focado): setas movem, Shift + setas redimensionam, Delete oculta, Esc cancela o arrasto.
 * - Persistência: GET/PUT `/dashboard/layouts/{key}` (empresa + usuário vêm do token). Ids ausentes no layout
 *   salvo entram no fim, visíveis e com o tamanho padrão; ids que não existem mais são descartados.
 *
 * Uso: `useDashboardLayout(key, registry)` com registry = [{ id, label, w, minW }] (ordem padrão);
 * na tela, `<DashboardBoard layout={layout}>` recebe `<Indicator id>` (direto ou em fragmentos).
 */

const COLS = 12;
const GAP = 12;
const ROW = 20;
const UNIT = ROW + GAP;
const MAX_ROWS = 90;
const NARROW_PX = 640;

const clone = (o) => JSON.parse(JSON.stringify(o));
const sortedOf = (L) => L.items.slice().sort((a, b) => a.order - b.order);
const sigOf = (L) => JSON.stringify(L.items.map((i) => [i.id, i.visible, i.order, i.w, i.h]));

function defaultLayout(registry) {
    return { items: registry.map((r, i) => ({ id: r.id, visible: true, order: i, w: r.w, h: null })) };
}

// Regra de merge: ids que não existem mais são descartados; indicadores novos entram no FIM, visíveis, com o tamanho padrão.
function mergeLayout(savedItems, registry) {
    const byId = new Map(registry.map((r) => [r.id, r]));
    if (!Array.isArray(savedItems) || savedItems.length === 0) return { layout: defaultLayout(registry), added: [] };
    const seen = new Set();
    const items = savedItems
        .map((i, idx) => ({ ...i, __idx: idx }))
        .filter((i) => i && byId.has(i.id) && !seen.has(i.id) && seen.add(i.id))
        .sort((a, b) => (Number.isFinite(a.order) && Number.isFinite(b.order) ? a.order - b.order : a.__idx - b.__idx))
        .map((i) => ({ id: i.id, visible: i.visible !== false, order: 0, w: i.w, h: i.h }));
    const added = [];
    registry.forEach((r) => {
        if (!seen.has(r.id)) { items.push({ id: r.id, visible: true, order: 0, w: r.w, h: null }); added.push(r.id); }
    });
    items.forEach((it, i) => {
        const r = byId.get(it.id);
        it.order = i;
        it.w = Math.max(r.minW, Math.min(COLS, Math.round(it.w) || r.w));
        it.h = it.h ? Math.max(1, Math.min(MAX_ROWS, Math.round(it.h))) : null;
    });
    return { layout: { items }, added };
}

export function useDashboardLayout(key, registry, enabled = true) {
    const reg = useMemo(() => Object.fromEntries(registry.map((r) => [r.id, r])), [registry]);
    const [viewL, setViewL] = useState(() => defaultLayout(registry));
    const [draft, setDraft] = useState(null);
    const draftRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [toast, setToast] = useState('');
    const [newIds, setNewIds] = useState([]);
    const boardRef = useRef(null);
    const barRef = useRef(null);
    const liveRef = useRef(null);
    const flipRef = useRef(null);
    const enterRef = useRef(null);
    const toastTimer = useRef(null);
    const defaultSig = useMemo(() => sigOf(defaultLayout(registry)), [registry]);

    useEffect(() => {
        if (!enabled) return undefined;
        let active = true;
        httpClient.get(`/dashboard/layouts/${key}`)
            .then((response) => {
                if (!active) return;
                const data = response.data || {};
                const m = mergeLayout(data.items, registry);
                setViewL(m.layout);
                setNewIds(data.saved ? m.added : []);
            })
            .catch((e) => console.error('Erro ao carregar o layout do dashboard:', e));
        return () => { active = false; };
    }, [key, enabled, registry]);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const editing = draft !== null;
    const current = draft || viewL;
    const announce = useCallback((m) => {
        const el = liveRef.current;
        if (!el) return;
        el.textContent = '';
        setTimeout(() => { el.textContent = m; }, 30);
    }, []);
    const notify = (m) => {
        setToast(m);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(''), 3200);
    };
    const captureRects = () => {
        const b = boardRef.current;
        if (!b) return null;
        return new Map([...b.querySelectorAll('.wg:not(.floating)')].map((e) => [e, e.getBoundingClientRect()]));
    };
    // Altera o rascunho (e anima os cards para a nova posição, estilo FLIP, depois do render).
    const mutate = (fn, flip = true) => {
        if (!draftRef.current) return;
        if (flip) flipRef.current = captureRects();
        const next = clone(draftRef.current);
        fn(next);
        draftRef.current = next;
        setDraft(next);
    };
    const renum = (L, arr) => { arr.forEach((it, i) => { it.order = i; }); L.items = arr; };

    const pendingCount = useMemo(() => {
        if (!draft) return 0;
        const a = new Map(viewL.items.map((i) => [i.id, i]));
        return draft.items.filter((i) => {
            const o = a.get(i.id);
            return !o || o.visible !== i.visible || o.order !== i.order || o.w !== i.w || o.h !== i.h;
        }).length;
    }, [draft, viewL]);
    const pending = pendingCount > 0;
    const isCustom = sigOf(viewL) !== defaultSig;
    const visibleCount = current.items.filter((i) => i.visible).length;
    const hiddenIds = sortedOf(current).filter((i) => !i.visible).map((i) => i.id);

    const start = () => {
        const d = clone(viewL);
        draftRef.current = d;
        setMsg(null);
        setDraft(d);
        announce('Modo de edição ativado. Arraste os cards para mover, puxe o canto para redimensionar.');
        setTimeout(() => barRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 0);
    };
    const exit = () => { draftRef.current = null; setDraft(null); setMsg(null); };
    const cancel = () => {
        if (saving) return;
        if (!pending) { exit(); return; }
        setMsg({ kind: 'ask' });
    };
    const discard = () => { exit(); notify('Alterações descartadas'); };
    const keepEditing = () => setMsg(null);

    const hide = (id) => {
        const it = draftRef.current?.items.find((i) => i.id === id);
        if (!it || !it.visible) return;
        const go = () => {
            mutate((L) => { L.items.find((i) => i.id === id).visible = false; });
            announce(`${reg[id].label} ocultado. Disponível na bandeja de ocultos.`);
        };
        const el = boardRef.current?.querySelector(`.wg[data-id="${id}"]`);
        const rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (el && !rm) { el.classList.add('wg-leave'); setTimeout(go, 160); } else go();
    };
    const hideAll = () => {
        const n = draftRef.current?.items.filter((i) => i.visible).length;
        if (!n) return;
        mutate((L) => { L.items.forEach((i) => { i.visible = false; }); });
        announce(`Todos os ${n} indicadores foram ocultados. Use Mostrar todos para reverter.`);
    };
    const showAll = () => {
        mutate((L) => { L.items.forEach((i) => { i.visible = true; }); });
        announce('Todos os indicadores voltaram ao dashboard');
    };
    const show = (id) => {
        enterRef.current = id;
        mutate((L) => {
            const arr = sortedOf(L);
            const it = arr.find((i) => i.id === id);
            if (!it || it.visible) return;
            it.visible = true;
            arr.splice(arr.indexOf(it), 1);
            arr.push(it);
            renum(L, arr);
        });
        announce(`${reg[id].label} devolvido ao dashboard, no fim`);
    };
    const moveBy = (id, dir) => {
        const L0 = draftRef.current;
        if (!L0) return false;
        const arr0 = sortedOf(L0);
        const i0 = arr0.findIndex((x) => x.id === id);
        let j0 = i0 + dir;
        while (arr0[j0] && !arr0[j0].visible) j0 += dir;
        if (!arr0[j0]) { announce(`Já está na ${dir < 0 ? 'primeira' : 'última'} posição`); return false; }
        mutate((L) => {
            const arr = sortedOf(L);
            const [it] = arr.splice(i0, 1);
            arr.splice(j0, 0, it);
            renum(L, arr);
        });
        const vis = arr0.filter((x) => x.visible);
        const k = vis.findIndex((x) => x.id === id) + dir + 1;
        announce(`${reg[id].label} movido para a posição ${k} de ${vis.length}`);
        return true;
    };
    // Aplica a ordem vista no DOM (só os visíveis); os ocultos ficam nos seus lugares.
    const setOrderFromIds = (ids) => {
        mutate((L) => {
            const arr = sortedOf(L);
            let k = 0;
            const byId = new Map(L.items.map((i) => [i.id, i]));
            renum(L, arr.map((it) => (it.visible ? byId.get(ids[k++]) || it : it)));
        }, false);
    };
    const setSize = (id, w, h) => {
        mutate((L) => {
            const it = L.items.find((i) => i.id === id);
            it.w = Math.max(reg[id].minW, Math.min(COLS, w));
            it.h = h ? Math.max(1, Math.min(MAX_ROWS, h)) : null;
        }, false);
    };
    const restoreDefault = () => {
        const d = defaultLayout(registry);
        if (sigOf(d) === sigOf(draftRef.current)) { notify('Já está no layout padrão'); return; }
        mutate((L) => { L.items = d.items; });
        setMsg(null);
        notify('Layout padrão aplicado ao rascunho · clique em Salvar para confirmar');
    };
    const save = async () => {
        if (saving || !pending) return;
        setSaving(true);
        setMsg(null);
        const L = clone(draftRef.current);
        const items = sortedOf(L).map((i, order) => ({ id: i.id, visible: i.visible, order, w: i.w, h: i.h }));
        try {
            const response = await httpClient.put(`/dashboard/layouts/${key}`, { schema: 2, items });
            if (response.status >= 200 && response.status < 300) {
                const next = { items };
                setViewL(next);
                setNewIds([]);
                exit();
                notify(sigOf(next) !== defaultSig ? 'Layout salvo na sua conta · vale em qualquer dispositivo' : 'Layout padrão restaurado e salvo');
            } else {
                setMsg({ kind: 'err' });
            }
        } catch (e) {
            console.error('Erro ao salvar o layout do dashboard:', e);
            setMsg({ kind: 'err' });
        } finally {
            setSaving(false);
        }
    };

    return {
        enabled, key, reg, editing, current, draft, viewL, draftRef, boardRef, barRef, liveRef, flipRef, enterRef,
        saving, msg, toast, newIds, pending, pendingCount, isCustom, visibleCount, hiddenIds,
        allHidden: visibleCount === 0,
        announce, start, cancel, discard, keepEditing, hide, hideAll, showAll, show, moveBy, setOrderFromIds, setSize, restoreDefault, save,
    };
}

/** Marcador de um indicador do board: `id` do registro; `children` = conteúdo do card. */
export function Indicator({ children }) {
    return children ?? null;
}

function flatten(children, out = []) {
    Children.forEach(children, (c) => {
        if (!isValidElement(c)) return;
        if (c.type === Indicator) out.push({ id: c.props.id, node: c.props.children });
        else if (c.type === Fragment) flatten(c.props.children, out);
    });
    return out;
}

// ---------------------------------------------------------------------------------------------- UI

const fadein = keyframes`from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}`;
const chipIn = keyframes`from{transform:scale(.8);opacity:0}to{transform:none;opacity:1}`;
const jig = keyframes`0%,100%{transform:rotate(0)}25%{transform:rotate(.18deg)}75%{transform:rotate(-.18deg)}`;
const wgIn = keyframes`from{opacity:0;transform:scale(.94)}to{opacity:1;transform:none}`;
const wgOut = keyframes`to{opacity:0;transform:scale(.92)}`;

const Svg = ({ size = 15, children, fill = 'none' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={fill === 'none' ? 'currentColor' : undefined} strokeWidth="2" aria-hidden="true">{children}</svg>
);
const EyeOffIcon = ({ size }) => <Svg size={size}><path d="M17.9 17.9A10.9 10.9 0 0 1 12 20c-7 0-11-8-11-8a19.8 19.8 0 0 1 5-5.9M9.9 4.2A10.7 10.7 0 0 1 12 4c7 0 11 8 11 8a19.7 19.7 0 0 1-3.2 4.2M1 1l22 22" /></Svg>;
const GripIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="9" cy="5" r="1.8" /><circle cx="15" cy="5" r="1.8" /><circle cx="9" cy="12" r="1.8" /><circle cx="15" cy="12" r="1.8" /><circle cx="9" cy="19" r="1.8" /><circle cx="15" cy="19" r="1.8" />
    </svg>
);

const shadowMd = '0 4px 14px rgba(15,23,42,.14)';
const shadowSm = '0 1px 3px rgba(15,23,42,.14)';
const shadowLg = '0 14px 34px rgba(15,23,42,.24)';

// Botão do protótipo (.btn): variantes sm / primary / ghost / danger.
function ProtoBtn({ variant, small, children, sx, ...rest }) {
    return (
        <Box component="button" type="button" {...rest}
            sx={(t) => ({
                height: small ? 34 : 40, px: small ? '12px' : '14px', borderRadius: '9px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary',
                fontSize: small ? '12.5px' : '13px', fontWeight: 600, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                '&:focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: '2px' },
                '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
                ...(variant === 'ghost' && { bgcolor: 'transparent' }),
                ...(variant === 'primary' && { background: t.palette.gradient.brand, borderColor: 'transparent', color: '#fff', '&:hover': { color: '#fff', filter: 'brightness(1.07)' }, '&:disabled': { opacity: 0.45, cursor: 'not-allowed', filter: 'none' } }),
                ...(variant === 'danger' && { bgcolor: 'error.main', borderColor: 'error.main', color: '#fff', '&:hover': { color: '#fff', filter: 'brightness(1.07)' } }),
                ...(typeof sx === 'function' ? sx(t) : sx),
            })}>
            {children}
        </Box>
    );
}

/** Chip "Layout padrão / Personalizado" ao lado do título. */
export function DashboardLayoutChip({ layout }) {
    if (!layout.enabled) return null;
    const custom = layout.isCustom;
    return (
        <Box component="span"
            title={custom ? 'Seu layout salvo. Disponível em qualquer dispositivo onde você entrar.' : 'Arranjo original do FlowVendas'}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, px: '10px', py: '3px', borderRadius: '20px', verticalAlign: 'middle', whiteSpace: 'nowrap', ml: 1,
                bgcolor: custom ? 'info.bg' : 'background.subtle', color: custom ? 'primary.main' : 'text.secondary', border: '1px solid', borderColor: custom ? 'transparent' : 'divider' }}>
            <Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: custom ? 'primary.main' : 'text.disabled' }} />
            {custom ? 'Personalizado' : 'Layout padrão'}
        </Box>
    );
}

/** Botão "Personalizar" (some durante a edição). */
export function DashboardPersonalizeButton({ layout }) {
    if (!layout.enabled || layout.editing) return null;
    return (
        <ProtoBtn onClick={layout.start} title="Escolher quais indicadores ver, a ordem e o tamanho dos cards">
            <Svg size={16}><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" /></Svg>
            Personalizar
        </ProtoBtn>
    );
}

/** Barra de edição (sticky) com a bandeja de ocultos. Só aparece no modo edição. */
export function DashboardEditBar({ layout }) {
    const { editing, pending, pendingCount, saving, msg, reg, hiddenIds, visibleCount } = layout;
    const [q, setQ] = useState('');
    const [had, setHad] = useState([]);
    const hiddenKey = hiddenIds.join(',');
    useEffect(() => { setHad(hiddenIds); }, [hiddenKey]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!layout.enabled || !editing) return null;
    const showSearch = hiddenIds.length > 8;
    const rows = hiddenIds.filter((id) => !showSearch || !q.trim() || reg[id].label.toLowerCase().includes(q.trim().toLowerCase()));
    return (
        <Box sx={{ position: 'sticky', top: 68, zIndex: 25, mb: '14px' }}>
            <Box ref={layout.barRef} role="region" aria-label="Barra de edição do dashboard"
                sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'primary.main', borderRadius: '14px', boxShadow: shadowMd, p: { xs: '8px 10px', md: '10px 14px' }, animation: `${fadein} .2s ease` }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 14px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 240 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Svg size={18}><path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" /></Svg>
                        </Box>
                        <Box>
                            <Box component="b" sx={{ display: 'block', fontSize: '13.5px' }}>Editando o dashboard</Box>
                            <Box component="span" sx={{ display: 'block', mt: '1px', fontSize: '12px', color: 'text.secondary', '& b': { fontWeight: 700, color: 'text.primary' } }}>
                                <b>Arraste o card</b> para mover · <b>puxe o canto</b> para mudar o tamanho ·{' '}
                                <Box component="span" tabIndex={0} title="Teclado: Tab foca um card · setas movem · Shift + setas redimensionam · Delete oculta · Esc cancela o arrasto" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>atalhos de teclado</Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box aria-live="polite" sx={{ fontSize: '11px', fontWeight: 700, px: '10px', py: '3px', borderRadius: '20px', whiteSpace: 'nowrap', bgcolor: pending ? 'warning.bg' : 'background.subtle', color: pending ? 'warning.main' : 'text.disabled' }}>
                        {pending ? `Alterações não salvas (${pendingCount})` : 'Sem alterações'}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', ml: 'auto' }}>
                        <ProtoBtn small variant="ghost" onClick={layout.hideAll} disabled={visibleCount === 0 || saving} title="Oculta todos os indicadores de uma vez (você pode devolvê-los pela bandeja ou em Mostrar todos)">
                            <EyeOffIcon size={15} /><span>Ocultar todos</span>
                        </ProtoBtn>
                        <ProtoBtn small variant="ghost" onClick={layout.restoreDefault} disabled={saving} title="Volta ao arranjo original do FlowVendas (só vale após Salvar)">
                            <Svg size={15}><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></Svg><span>Restaurar padrão</span>
                        </ProtoBtn>
                        <ProtoBtn small onClick={layout.cancel} disabled={saving}>Cancelar</ProtoBtn>
                        <ProtoBtn small variant="primary" onClick={layout.save} disabled={saving || !pending}>{saving ? 'Salvando…' : 'Salvar layout'}</ProtoBtn>
                    </Box>
                </Box>
                {msg && (
                    <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', p: '10px 12px', borderRadius: '10px', fontSize: '12.5px', fontWeight: 600, mt: '10px',
                        bgcolor: msg.kind === 'err' ? 'error.bg' : 'warning.bg', color: msg.kind === 'err' ? 'error.main' : 'text.primary' }}>
                        {msg.kind === 'err' ? (
                            <>
                                <Box component="span" sx={{ flex: 1, minWidth: 160 }}>Não foi possível salvar o layout (falha de conexão). Suas alterações continuam aqui.</Box>
                                <ProtoBtn small onClick={layout.save}>Tentar novamente</ProtoBtn>
                            </>
                        ) : (
                            <>
                                <Box component="span" sx={{ flex: 1, minWidth: 160 }}>Descartar as alterações não salvas? O dashboard volta ao último layout salvo.</Box>
                                <ProtoBtn small onClick={layout.keepEditing} autoFocus>Continuar editando</ProtoBtn>
                                <ProtoBtn small variant="danger" onClick={layout.discard}>Descartar alterações</ProtoBtn>
                            </>
                        )}
                    </Box>
                )}
                {hiddenIds.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '10px', pt: '10px', borderTop: '1px dashed', borderColor: 'divider', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                        <Box component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', '& svg': { color: 'text.disabled' } }}>
                            <EyeOffIcon size={15} /><span>Ocultos ({hiddenIds.length})</span>
                        </Box>
                        {showSearch && (
                            <Box component="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." aria-label="Buscar entre os indicadores ocultos"
                                sx={{ width: 150, height: 28, border: '1px solid', borderColor: 'divider', borderRadius: '20px', bgcolor: 'background.subtle', color: 'text.primary', px: '11px', fontSize: '12px', fontFamily: 'inherit', flexShrink: 0, '&:focus': { outline: 'none', borderColor: 'primary.main' } }} />
                        )}
                        <Box role="list" sx={{ display: 'flex', gap: '6px', overflowX: 'auto', flex: 1, minWidth: 0, p: '2px', scrollbarWidth: 'thin', flexBasis: { xs: '100%', md: 'auto' }, order: { xs: 3, md: 0 } }}>
                            {rows.length ? rows.map((id) => (
                                <Box key={id} component="button" type="button" role="listitem" title="Devolver ao dashboard" aria-label={`Mostrar ${reg[id].label} de volta no dashboard`}
                                    onClick={() => layout.show(id)}
                                    sx={(t) => ({
                                        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '6px', height: 28, pl: '8px', pr: '10px', borderRadius: '20px', border: '1px dashed', borderColor: 'text.disabled', bgcolor: 'background.subtle',
                                        color: 'text.secondary', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap', transition: '.15s',
                                        ...(had.includes(id) ? {} : { animation: `${chipIn} .2s ease` }),
                                        '&:hover': { borderColor: 'primary.main', borderStyle: 'solid', color: 'primary.main', bgcolor: 'info.bg' },
                                        '&:focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: '2px' },
                                    })}>
                                    <Svg size={12}><path d="M12 5v14M5 12h14" /></Svg>{reg[id].label}
                                </Box>
                            )) : <Box component="span" sx={{ fontSize: '12px', color: 'text.disabled', p: '5px 4px' }}>Nenhum oculto com esse nome.</Box>}
                        </Box>
                        <Box component="button" type="button" onClick={layout.showAll} disabled={saving}
                            sx={{ border: 0, bgcolor: 'transparent', color: 'primary.main', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                            Mostrar todos
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

const boardSx = (t) => {
    const P = t.palette.primary.main;
    const mix = (pct) => `color-mix(in srgb, ${P} ${pct}%, transparent)`;
    return {
        display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gridAutoRows: `${ROW}px`, gap: `${GAP}px`, gridAutoFlow: 'row dense', position: 'relative',
        '&.narrow': { gridTemplateColumns: 'minmax(0, 1fr)' },
        '& .wg': { minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative', borderRadius: '14px' },
        '& .wg.measure': { gridRow: 'span 1 !important', alignSelf: 'start !important', height: 'auto !important' },
        '& .wg-body': { flex: 1, display: 'flex', minWidth: 0, minHeight: 0 },
        '& .wg-body > *': { flex: 1, minWidth: 0, minHeight: 0, margin: 0 },
        '& .wg-body > .MuiCard-root': { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        '& .wg-body > .MuiCard-root > .MuiCardContent-root': { flex: 1 },
        '& .new-tag': { display: 'none' },
        '& .wg.is-new .new-tag': { display: 'block', position: 'absolute', top: -8, left: 12, zIndex: 3, bgcolor: 'primary.main', color: '#fff', fontSize: '9.5px', fontWeight: 800, px: '8px', py: '2px', borderRadius: '10px' },
        '& .wg-enter': { animation: `${wgIn} .22s ease` },
        '& .wg-leave': { animation: `${wgOut} .16s ease forwards` },
        '& .wg-ov': { display: 'none' },
        '&.editing .wg-body': { userSelect: 'none', animation: `${jig} 2.6s ease-in-out infinite`, transformOrigin: '50% 50%' },
        '&.editing .wg:nth-of-type(3n) .wg-body': { animationDelay: '-.7s' },
        '&.editing .wg:nth-of-type(3n+1) .wg-body': { animationDelay: '-1.5s', animationDuration: '3s' },
        '&.editing .wg:hover .wg-body, &.editing .wg:focus-visible .wg-body, &.editing .wg.floating .wg-body': { animation: 'none' },
        '&.editing .wg-ov': {
            display: 'block', position: 'absolute', inset: 0, borderRadius: '14px', border: `1.5px dashed ${mix(55)}`, bgcolor: 'transparent', cursor: 'grab', touchAction: 'pan-y',
            transition: 'background .15s, border-color .15s, box-shadow .15s', zIndex: 2,
        },
        '&.editing .wg:hover .wg-ov': { borderStyle: 'solid', borderColor: P, bgcolor: mix(5), boxShadow: `0 0 0 3px ${mix(14)}` },
        '&.editing .wg:focus-visible': { outline: `2px solid ${P}`, outlineOffset: '3px' },
        '& .ov-name': {
            position: 'absolute', top: -13, left: 10, maxWidth: 'calc(100% - 60px)', display: 'flex', alignItems: 'center', gap: '6px', height: 24, pl: '8px', pr: '10px', borderRadius: '12px', bgcolor: 'primary.main', color: '#fff',
            boxShadow: shadowMd, fontSize: '11px', fontWeight: 700, pointerEvents: 'none', opacity: 0, transform: 'translateY(3px)', transition: 'opacity .15s, transform .15s',
            '& span': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, '& svg': { flexShrink: 0, opacity: 0.8 },
        },
        '&.editing .wg:hover .ov-name, &.editing .wg:focus-visible .ov-name, &.editing .wg.floating .ov-name': { opacity: 1, transform: 'none' },
        '& .ov-tools': { position: 'absolute', top: -11, right: -8, display: 'flex', gap: '4px', zIndex: 4 },
        '& .hide-btn, & .mv': {
            width: 24, height: 24, border: '1px solid', borderColor: t.palette.divider, bgcolor: t.palette.background.paper, color: t.palette.text.secondary, borderRadius: '50%', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0, boxShadow: shadowSm, touchAction: 'manipulation',
            '&:focus-visible': { outline: `2px solid ${P}`, outlineOffset: '2px' },
        },
        '& .hide-btn:hover': { borderColor: t.palette.error.main, color: t.palette.error.main, bgcolor: t.palette.error.bg },
        '& .mv': { display: 'none' }, '&.narrow .mv': { display: 'flex' },
        '& .mv:hover': { borderColor: P, color: P },
        '& .rz': { position: 'absolute', zIndex: 3, touchAction: 'none' },
        '& .rz-se': { right: 5, bottom: 5, width: 20, height: 20, borderRadius: '7px', bgcolor: 'primary.main', color: '#fff', cursor: 'nwse-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: shadowMd, transition: 'transform .15s', '&:hover': { transform: 'scale(1.12)' } },
        '& .rz-e': { top: 40, bottom: 32, right: -7, width: 14, cursor: 'ew-resize' },
        '& .rz-s': { left: 40, right: 32, bottom: -7, height: 14, cursor: 'ns-resize' },
        '& .rz-e::after, & .rz-s::after': { content: '""', position: 'absolute', bgcolor: 'primary.main', borderRadius: '3px', opacity: 0.55, transition: 'opacity .15s' },
        '& .rz-e::after': { top: '50%', left: 5, width: 4, height: 30, transform: 'translateY(-50%)' },
        '& .rz-s::after': { left: '50%', top: 5, height: 4, width: 30, transform: 'translateX(-50%)' },
        '& .rz-e:hover::after, & .rz-s:hover::after': { opacity: 1 },
        '&.narrow .rz': { display: 'none' },
        '& .sz-badge': { display: 'none', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 5, bgcolor: 'text.primary', color: 'background.default', fontSize: '12px', fontWeight: 700, px: '11px', py: '5px', borderRadius: '9px', whiteSpace: 'nowrap', boxShadow: shadowMd, pointerEvents: 'none', fontVariantNumeric: 'tabular-nums' },
        '& .wg.rsz .sz-badge': { display: 'block' },
        '& .sz-badge em': { fontStyle: 'normal', opacity: 0.7, fontWeight: 600 },
        '& .wg.floating': { position: 'absolute', zIndex: 60, gridColumn: 'auto !important', gridRow: 'auto !important', margin: 0 },
        '& .wg.floating:not(.rsz)': { transform: 'scale(1.025)' },
        '& .wg.floating .wg-body': { boxShadow: shadowLg, borderRadius: '14px' },
        '& .wg.floating .wg-ov': { borderStyle: 'solid', borderColor: P, cursor: 'grabbing' },
        '& .wg.floating.rsz': { transition: 'left .16s ease, top .16s ease' },
        '& .wg.floating.rsz .wg-ov': { cursor: 'inherit' },
        '& .wg.floating.settling': { transition: 'left .18s ease, top .18s ease, width .18s ease, height .18s ease, transform .18s ease', transform: 'none' },
        '& .wg-ph': { border: `2px dashed ${P}`, bgcolor: mix(9), borderRadius: '14px' },
        '& .board-empty': { gridColumn: '1 / -1', gridRow: 'span 8', textAlign: 'center', p: '48px 20px', border: '1px dashed', borderColor: t.palette.divider, borderRadius: '14px', color: t.palette.text.secondary, fontSize: '13.5px', '& b': { display: 'block', fontSize: '15px', color: t.palette.text.primary, mb: '6px' }, '& button': { mt: '14px' } },
        '@media (prefers-reduced-motion: reduce)': {
            '&.editing .wg-body': { animation: 'none !important' }, '& .wg-enter, & .wg-leave': { animation: 'none !important' },
            '& .wg.floating.settling, & .wg.floating.rsz, &.editing .wg-ov, & .rz-se': { transition: 'none !important' },
        },
    };
};

function getScrollParent(el) {
    let p = el.parentElement;
    while (p && p !== document.body) {
        const oy = getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
        p = p.parentElement;
    }
    return window;
}

// Define colunas e linhas de cada card. Altura mínima = conteúdo (medido com o card em altura automática).
function fitBoard(board, L, narrow) {
    const els = new Map([...board.children].filter((e) => e.classList.contains('wg')).map((e) => [e.dataset.id, e]));
    const list = [];
    L.items.forEach((it) => {
        const el = els.get(it.id);
        if (!el || !it.visible || el.classList.contains('floating')) return;
        el.style.gridColumn = `span ${narrow ? COLS : it.w}`;
        el.classList.add('measure');
        list.push([el, it]);
    });
    const nat = list.map(([el]) => el.offsetHeight);
    list.forEach(([el, it], i) => {
        el.classList.remove('measure');
        const min = Math.ceil((nat[i] + GAP) / UNIT);
        const eff = Math.max(it.h || 0, min);
        el.style.gridRow = `span ${eff}`;
        el._min = min;
        el._eff = eff;
    });
    fillGaps(board, list);
}

// Preenche o vazio abaixo de cada card até o próximo card nas mesmas colunas (ou até o fim do board),
// para que cards de alturas diferentes fechem a faixa sem deixar espaço em branco. `_eff`/`_min` não mudam.
function fillGaps(board, list) {
    if (!list.length) return;
    const cell = (board.clientWidth - GAP * (COLS - 1)) / COLS + GAP;
    const boxes = list.map(([el]) => {
        const c0 = Math.round(el.offsetLeft / cell);
        const r0 = Math.round(el.offsetTop / UNIT);
        return { el, c0, c1: Math.max(c0 + 1, Math.round((el.offsetLeft + el.offsetWidth + GAP) / cell)), r0, r1: r0 + el._eff };
    });
    const bottom = Math.max(...boxes.map((b) => b.r1));
    boxes.forEach((b) => {
        let next = bottom;
        boxes.forEach((o) => {
            if (o !== b && o.r0 >= b.r1 && o.c0 < b.c1 && o.c1 > b.c0 && o.r0 < next) next = o.r0;
        });
        if (next > b.r1) b.el.style.gridRow = `span ${b.el._eff + next - b.r1}`;
    });
}

// FLIP: anima os cards de onde estavam (rects capturados antes da mudança) até a nova posição.
function playFlip(board, first) {
    if (!first || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const moves = [];
    [...board.querySelectorAll('.wg:not(.floating)')].forEach((e) => {
        const a = first.get(e);
        if (!a) return;
        const b = e.getBoundingClientRect();
        const dx = a.left - b.left;
        const dy = a.top - b.top;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
            e.style.transition = 'none';
            e.style.transform = `translate(${dx}px,${dy}px)`;
            moves.push(e);
        }
    });
    if (!moves.length) return;
    void board.offsetHeight;
    moves.forEach((e) => { e.style.transition = 'transform 200ms cubic-bezier(.2,.8,.2,1)'; e.style.transform = ''; });
    setTimeout(() => moves.forEach((e) => { if (e.style.transform === '') e.style.transition = ''; }), 240);
}

/** O board: renderiza os `<Indicator>` filhos na ordem/tamanho do layout, com a interação de edição. */
export function DashboardBoard({ layout, children }) {
    const { editing, current, reg, newIds } = layout;
    const boardRef = layout.boardRef;
    const [narrow, setNarrow] = useState(false);
    const widgets = useMemo(() => new Map(flatten(children).map((w) => [w.id, w.node])), [children]);
    const ordered = sortedOf(current).filter((it) => it.visible && widgets.has(it.id));
    const layoutRef = useRef(layout);
    layoutRef.current = layout;
    const currentRef = useRef(current);
    currentRef.current = current;
    const narrowRef = useRef(narrow);
    narrowRef.current = narrow;
    const busy = useRef(false);

    const refit = useCallback(() => {
        const b = boardRef.current;
        if (!b || busy.current) return;
        fitBoard(b, currentRef.current, narrowRef.current);
    }, [boardRef]);

    // Observa cada card: se o conteúdo mudar de altura fora de um render (gráfico, fonte, imagem), refaz o encaixe.
    const cardRo = useRef(null);
    const observed = useRef(new Set());
    const syncCards = useCallback(() => {
        const ro = cardRo.current;
        const b = boardRef.current;
        if (!ro || !b) return;
        const now = new Set([...b.children].filter((e) => e.classList.contains('wg')));
        observed.current.forEach((e) => { if (!now.has(e)) { ro.unobserve(e); observed.current.delete(e); } });
        now.forEach((e) => { if (!observed.current.has(e)) { ro.observe(e); observed.current.add(e); } });
    }, [boardRef]);

    useEffect(() => {
        let raf = 0;
        const ro = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(refit); });
        cardRo.current = ro;
        const seen = observed.current;
        syncCards();
        return () => { cancelAnimationFrame(raf); ro.disconnect(); seen.clear(); cardRo.current = null; };
    }, [refit, syncCards]);

    // Refaz o encaixe a cada render (dados novos, ordem, tamanho) e anima mudanças de posição.
    useLayoutEffect(() => {
        const b = boardRef.current;
        if (!b) return;
        refit();
        syncCards();
        const first = layoutRef.current.flipRef.current;
        layoutRef.current.flipRef.current = null;
        playFlip(b, first);
        const enterId = layoutRef.current.enterRef.current;
        if (enterId) {
            layoutRef.current.enterRef.current = null;
            const el = b.querySelector(`.wg[data-id="${enterId}"]`);
            if (el) {
                el.classList.add('wg-enter');
                setTimeout(() => el.classList.remove('wg-enter'), 260);
                el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    });

    useEffect(() => {
        const b = boardRef.current;
        if (!b) return undefined;
        const ro = new ResizeObserver(() => {
            const n = b.clientWidth < NARROW_PX;
            if (n !== narrowRef.current) setNarrow(n); else refit();
        });
        ro.observe(b);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit);
        return () => ro.disconnect();
    }, [boardRef, refit]);

    // ----- interação: arrastar (mover), redimensionar e teclado (apenas no modo edição) -----
    useEffect(() => {
        const board = boardRef.current;
        if (!board || !editing) return undefined;
        const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
        const DUR = () => (RM.matches ? 0 : 200);
        const scroller = getScrollParent(board);
        const wgOf = (id) => board.querySelector(`.wg[data-id="${id}"]`);
        const L = () => layoutRef.current.draftRef.current;
        const labelOf = (id) => layoutRef.current.reg[id]?.label || id;
        const isNarrow = () => board.classList.contains('narrow');
        let D = null;
        let R = null;
        let P0 = null;
        const last = { x: 0, y: 0 };
        let rafOn = false;

        const visRange = () => {
            const bar = layoutRef.current.barRef.current;
            const bb = bar ? bar.getBoundingClientRect().bottom : 0;
            if (scroller === window) return { top: Math.max(68, bb), bottom: window.innerHeight };
            const r = scroller.getBoundingClientRect();
            return { top: Math.max(r.top, bb), bottom: Math.min(r.bottom, window.innerHeight) };
        };
        const scrollDoc = (dy) => { if (scroller === window) window.scrollBy(0, dy); else scroller.scrollTop += dy; };

        const flip = (fn) => {
            const els = [...board.querySelectorAll('.wg:not([hidden]):not(.floating), .wg-ph')];
            const first = new Map(els.map((e) => [e, e.getBoundingClientRect()]));
            fn();
            const d = DUR();
            if (!d) return;
            els.forEach((e) => { e.style.transition = 'none'; e.style.transform = ''; });
            const moves = [];
            els.forEach((e) => {
                const a = first.get(e);
                const b = e.getBoundingClientRect();
                const dx = a.left - b.left;
                const dy = a.top - b.top;
                if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) { e.style.transform = `translate(${dx}px,${dy}px)`; moves.push(e); }
            });
            if (!moves.length) return;
            void board.offsetHeight;
            moves.forEach((e) => { e.style.transition = `transform ${d}ms cubic-bezier(.2,.8,.2,1)`; e.style.transform = ''; });
            setTimeout(() => moves.forEach((e) => { if (e.style.transform === '') e.style.transition = ''; }), d + 40);
        };

        const floatCard = (wg) => {
            const br = board.getBoundingClientRect();
            const r = wg.getBoundingClientRect();
            const ph = document.createElement('div');
            ph.className = 'wg-ph';
            ph.style.gridColumn = wg.style.gridColumn;
            ph.style.gridRow = wg.style.gridRow;
            wg.before(ph);
            wg.style.left = `${r.left - br.left}px`;
            wg.style.top = `${r.top - br.top}px`;
            wg.style.width = `${r.width}px`;
            wg.style.height = `${r.height}px`;
            wg.classList.add('floating');
            busy.current = true;
            return { ph, r };
        };
        const settle = (wg, ph, done) => {
            const br = board.getBoundingClientRect();
            const r = ph.getBoundingClientRect();
            const fin = () => {
                wg.classList.remove('floating', 'settling', 'rsz');
                ['left', 'top', 'width', 'height', 'transform', 'transition'].forEach((p) => { wg.style[p] = ''; });
                wg.style.gridColumn = ph.style.gridColumn;
                wg.style.gridRow = ph.style.gridRow;
                ph.replaceWith(wg);
                busy.current = false;
                if (done) done();
            };
            if (!DUR()) { fin(); return; }
            wg.classList.add('settling');
            wg.style.left = `${r.left - br.left}px`;
            wg.style.top = `${r.top - br.top}px`;
            wg.style.width = `${r.width}px`;
            wg.style.height = `${r.height}px`;
            setTimeout(fin, DUR() + 30);
        };

        // --- mover
        const startDrag = (wg, ov, pid, x, y, touch) => {
            const f = floatCard(wg);
            D = { wg, ph: f.ph, ox: x - f.r.left, oy: y - f.r.top, t: 0, orig: [...board.children].indexOf(f.ph), origNext: f.ph.nextElementSibling };
            try { ov.setPointerCapture(pid); } catch (_) { /* sem captura */ }
            document.body.classList.add('is-dragging');
            last.x = x; last.y = y;
            startLoop();
            if (touch && navigator.vibrate) navigator.vibrate(12);
            layoutRef.current.announce(`Movendo ${labelOf(wg.dataset.id)}`);
        };
        const reorderCheck = (cx, cy) => {
            if (performance.now() - D.t < 150) return;
            const ph = D.ph;
            const others = [...board.querySelectorAll('.wg:not([hidden]):not(.floating)')];
            const inR = (r) => cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom;
            if (inR(ph.getBoundingClientRect())) return;
            const t = others.find((e) => inR(e.getBoundingClientRect()));
            if (t) {
                const after = ph.compareDocumentPosition(t) & Node.DOCUMENT_POSITION_FOLLOWING;
                D.t = performance.now();
                flip(() => { if (after) t.after(ph); else t.before(ph); });
                return;
            }
            const lastEl = others[others.length - 1];
            if (lastEl && cy > lastEl.getBoundingClientRect().bottom + 4 && board.lastElementChild !== ph) {
                D.t = performance.now();
                flip(() => board.appendChild(ph));
            }
        };
        const dragMove = (cx, cy) => {
            const br = board.getBoundingClientRect();
            D.wg.style.left = `${cx - br.left - D.ox}px`;
            D.wg.style.top = `${cy - br.top - D.oy}px`;
            reorderCheck(cx, cy);
        };
        const endDrag = (drop) => {
            const d = D;
            D = null;
            document.body.classList.remove('is-dragging');
            if (!drop) {
                flip(() => { if (d.origNext && d.origNext.parentNode === board) board.insertBefore(d.ph, d.origNext); else board.appendChild(d.ph); });
            }
            settle(d.wg, d.ph, () => {
                const ids = [...board.querySelectorAll('.wg')].map((e) => e.dataset.id);
                const before = [...L().items].sort((a, b) => a.order - b.order).filter((i) => i.visible).map((i) => i.id).join(',');
                const moved = ids.join(',') !== before;
                layoutRef.current.setOrderFromIds(ids);
                d.wg.classList.add('wg-enter');
                setTimeout(() => d.wg.classList.remove('wg-enter'), 260);
                d.wg.focus({ preventScroll: true });
                layoutRef.current.announce(moved ? `${labelOf(d.wg.dataset.id)} movido para a posição ${ids.indexOf(d.wg.dataset.id) + 1}` : 'Movimento cancelado');
            });
        };

        // --- redimensionar
        const showBadge = () => {
            const atMin = R.cols <= R.r0.minW || R.rows <= R.minRows;
            let em = '';
            if (atMin) {
                const wMin = R.cols <= R.r0.minW;
                const hMin = R.rows <= R.minRows;
                em = ` <em>· ${wMin && hMin ? 'tamanho mínimo' : wMin ? 'largura mínima' : 'altura mínima'}</em>`;
            }
            R.badge.innerHTML = `${R.cols} col × ${R.rows} lin${em}`;
        };
        const anchor = () => {
            const br = board.getBoundingClientRect();
            const pr = R.ph.getBoundingClientRect();
            R.wg.style.left = `${pr.left - br.left}px`;
            R.wg.style.top = `${pr.top - br.top}px`;
        };
        const natAt = (cols) => {
            const wg = R.wg;
            const w = wg.style.width;
            const h = wg.style.height;
            wg.style.width = `${cols * R.colW + (cols - 1) * GAP}px`;
            wg.style.height = 'auto';
            const n = wg.offsetHeight;
            wg.style.width = w;
            wg.style.height = h;
            return n;
        };
        const startResize = (wg, handle, e) => {
            const id = wg.dataset.id;
            const it = L().items.find((i) => i.id === id);
            const r0 = layoutRef.current.reg[id];
            const mode = handle.dataset.rz;
            const f = floatCard(wg);
            const br = board.getBoundingClientRect();
            const colW = (br.width - GAP * (COLS - 1)) / COLS;
            R = {
                wg, ph: f.ph, id, r0, mode, x0: e.clientX, y0: e.clientY, w0: f.r.width, h0: f.r.height, step: colW + GAP, colW,
                cols: it.w, rows: wg._eff, nat: f.r.height, minRows: wg._min, rows0: wg._eff, cols0: it.w, badge: wg.querySelector('.sz-badge'),
            };
            wg.classList.add('rsz');
            document.body.classList.add('is-resizing');
            try { handle.setPointerCapture(e.pointerId); } catch (_) { /* sem captura */ }
            last.x = e.clientX; last.y = e.clientY;
            showBadge();
            startLoop();
        };
        const rzMove = (cx, cy) => {
            const br = board.getBoundingClientRect();
            const wantW = R.mode.indexOf('e') > -1;
            const wantH = R.mode.indexOf('s') > -1;
            const minPx = R.r0.minW * R.colW + (R.r0.minW - 1) * GAP;
            const nw = wantW ? Math.max(minPx, Math.min(br.width, R.w0 + cx - R.x0)) : R.w0;
            let nh = wantH ? R.h0 + cy - R.y0 : R.h0;
            const cols = wantW ? Math.max(R.r0.minW, Math.min(COLS, Math.round((nw + GAP) / R.step))) : R.cols0;
            if (cols !== R.cols) { R.cols = cols; R.nat = natAt(cols); R.minRows = Math.ceil((R.nat + GAP) / UNIT); }
            nh = Math.max(nh, R.nat);
            const rows = wantH ? Math.max(R.minRows, Math.min(MAX_ROWS, Math.round((nh + GAP) / UNIT))) : Math.max(R.minRows, R.rows0);
            R.wg.style.width = `${nw}px`;
            R.wg.style.height = `${wantH ? nh : Math.max(R.nat, rows * UNIT - GAP)}px`;
            if (cols !== R.snapC || rows !== R.snapR) {
                R.snapC = cols; R.snapR = rows;
                flip(() => { R.ph.style.gridColumn = `span ${cols}`; R.ph.style.gridRow = `span ${rows}`; });
            }
            R.rows = rows;
            showBadge();
            anchor();
        };
        const endResize = (commit) => {
            const r = R;
            R = null;
            document.body.classList.remove('is-resizing');
            if (!commit) { r.ph.style.gridColumn = `span ${r.cols0}`; r.ph.style.gridRow = `span ${r.rows0}`; }
            settle(r.wg, r.ph, () => {
                r.wg.classList.remove('rsz');
                if (commit) {
                    const it = L().items.find((i) => i.id === r.id);
                    const h = r.mode.indexOf('s') > -1 ? (r.rows <= r.minRows ? null : r.rows) : it.h;
                    layoutRef.current.setSize(r.id, r.cols, h);
                }
                r.wg.focus({ preventScroll: true });
                layoutRef.current.announce(`${labelOf(r.id)}: ${commit ? r.cols : r.cols0} colunas, ${commit ? r.rows : r.rows0} linhas`);
            });
        };

        // --- rolagem automática durante o arrasto / redimensionamento
        const tick = () => {
            if (!(D || R)) { rafOn = false; return; }
            const v = visRange();
            const edge = 64;
            let dy = 0;
            if (last.y < v.top + edge + 8) dy = -Math.min(18, Math.ceil((v.top + edge + 8 - last.y) / edge * 18) + 2);
            else if (last.y > v.bottom - edge) dy = Math.min(18, Math.ceil((last.y - (v.bottom - edge)) / edge * 18) + 2);
            if (dy) { scrollDoc(dy); if (D) dragMove(last.x, last.y); if (R) rzMove(last.x, last.y); }
            if (D) reorderCheck(last.x, last.y);
            if (R) anchor();
            requestAnimationFrame(tick);
        };
        function startLoop() { if (!rafOn) { rafOn = true; requestAnimationFrame(tick); } }

        // --- eventos
        const onDown = (e) => {
            if (D || R || e.button > 0) return;
            const wg = e.target.closest('.wg');
            if (!wg || wg.classList.contains('floating')) return;
            if (e.target.closest('.hide-btn,.mv')) return;
            const h = e.target.closest('[data-rz]');
            if (h) { if (isNarrow()) return; e.preventDefault(); startResize(wg, h, e); return; }
            if (!e.target.closest('.wg-ov')) return;
            const touch = e.pointerType !== 'mouse';
            P0 = { wg, x: e.clientX, y: e.clientY, id: e.pointerId, ov: e.target.closest('.wg-ov'), touch, timer: null };
            if (touch) P0.timer = setTimeout(() => { if (P0) { const p = P0; P0 = null; startDrag(p.wg, p.ov, p.id, p.x, p.y, true); } }, 320);
        };
        const onMove = (e) => {
            last.x = e.clientX; last.y = e.clientY;
            if (P0) {
                const dx = e.clientX - P0.x;
                const dy = e.clientY - P0.y;
                if (!P0.touch && Math.hypot(dx, dy) > 4) { const p = P0; P0 = null; startDrag(p.wg, p.ov, p.id, p.x, p.y, false); }
                else if (P0.touch && Math.hypot(dx, dy) > 9) { clearTimeout(P0.timer); P0 = null; }
            }
            if (D) dragMove(e.clientX, e.clientY); else if (R) rzMove(e.clientX, e.clientY);
        };
        const onUp = () => {
            if (P0) { clearTimeout(P0.timer); P0 = null; }
            if (D) endDrag(true); else if (R) endResize(true);
        };
        const onCancel = () => { if (D) endDrag(false); else if (R) endResize(false); };
        const onEsc = (e) => { if (e.key === 'Escape') { if (D) endDrag(false); else if (R) endResize(false); } };
        const onTouchMove = (e) => { if (D || R) e.preventDefault(); };
        const onCtx = (e) => { if (D || P0 || R) e.preventDefault(); };

        const onKey = (e) => {
            if (!e.target.classList || !e.target.classList.contains('wg')) return;
            const id = e.target.dataset.id;
            const k = e.key;
            const api = layoutRef.current;
            if (k === 'Delete' || k === 'Backspace') { e.preventDefault(); api.hide(id); return; }
            if (!['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(k)) return;
            e.preventDefault();
            if (e.shiftKey) {
                const it = L().items.find((i) => i.id === id);
                const el = wgOf(id);
                const dw = k === 'ArrowRight' ? 1 : k === 'ArrowLeft' ? -1 : 0;
                const dh = k === 'ArrowDown' ? 1 : k === 'ArrowUp' ? -1 : 0;
                const w = Math.max(api.reg[id].minW, Math.min(COLS, it.w + dw));
                let h = it.h;
                if (dh) { const nh = Math.max(el._min, el._eff + dh); h = nh <= el._min ? null : nh; }
                api.setSize(id, w, h);
                api.announce(`${labelOf(id)}: ${w} colunas${dh ? `, ${h || el._min} linhas` : ''}`);
                return;
            }
            if (api.moveBy(id, (k === 'ArrowUp' || k === 'ArrowLeft') ? -1 : 1)) {
                requestAnimationFrame(() => {
                    const el = wgOf(id);
                    if (el) { el.focus({ preventScroll: true }); el.scrollIntoView({ block: 'nearest', behavior: RM.matches ? 'auto' : 'smooth' }); }
                });
            }
        };

        board.addEventListener('pointerdown', onDown);
        board.addEventListener('keydown', onKey);
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onCancel);
        document.addEventListener('keydown', onEsc);
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('contextmenu', onCtx);
        return () => {
            board.removeEventListener('pointerdown', onDown);
            board.removeEventListener('keydown', onKey);
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onCancel);
            document.removeEventListener('keydown', onEsc);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('contextmenu', onCtx);
            document.body.classList.remove('is-dragging', 'is-resizing');
            busy.current = false;
        };
    }, [editing, boardRef]);

    // Cursor/seleção globais durante arrasto (classes no body, como no protótipo).
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = 'body.is-dragging, body.is-dragging *{cursor:grabbing !important;user-select:none !important;} body.is-resizing, body.is-resizing *{user-select:none !important;}';
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    const sr = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };
    return (
        <>
            <Box ref={boardRef} className={`board${narrow ? ' narrow' : ''}${editing ? ' editing' : ''}`} sx={boardSx}>
                {ordered.map((it) => {
                    const label = reg[it.id].label;
                    return (
                        <div key={it.id} className={`wg${newIds.includes(it.id) ? ' is-new' : ''}`} data-id={it.id} role="group" aria-roledescription="card reordenável" aria-label={label} tabIndex={editing ? 0 : undefined}>
                            <span className="new-tag">Novo</span>
                            <div className="wg-body">{widgets.get(it.id)}</div>
                            {editing && (
                                <div className="wg-ov">
                                    <div className="ov-name"><GripIcon /><span>{label}</span></div>
                                    <div className="ov-tools">
                                        <button type="button" className="mv" tabIndex={-1} aria-label={`Mover ${label} para antes`} title="Mover para antes" onClick={() => layout.moveBy(it.id, -1)}>
                                            <Svg size={14}><path d="m18 15-6-6-6 6" /></Svg>
                                        </button>
                                        <button type="button" className="mv" tabIndex={-1} aria-label={`Mover ${label} para depois`} title="Mover para depois" onClick={() => layout.moveBy(it.id, 1)}>
                                            <Svg size={14}><path d="m6 9 6 6 6-6" /></Svg>
                                        </button>
                                        <button type="button" className="hide-btn" tabIndex={-1} aria-label={`Ocultar ${label}`} title="Ocultar (volta pela bandeja)" onClick={() => layout.hide(it.id)}>
                                            <EyeOffIcon size={14} />
                                        </button>
                                    </div>
                                    <span className="rz rz-e" data-rz="e" title="Arraste para ajustar a largura" />
                                    <span className="rz rz-s" data-rz="s" title="Arraste para ajustar a altura" />
                                    <span className="rz rz-se" data-rz="se" title="Arraste para redimensionar"><Svg size={12}><path d="M21 15v6h-6M9 3H3v6" /></Svg></span>
                                    <span className="sz-badge" />
                                </div>
                            )}
                        </div>
                    );
                })}
                {layout.allHidden && (
                    <div className="board-empty">
                        <b>Nenhum indicador visível</b>
                        {editing ? (
                            <>
                                Todos os indicadores estão ocultos. Escolha um na bandeja “Ocultos” acima ou mostre todos de uma vez.
                                <br />
                                <ProtoBtn small variant="primary" onClick={layout.showAll}>Mostrar todos</ProtoBtn>
                            </>
                        ) : 'Clique em Personalizar para escolher o que mostrar.'}
                    </div>
                )}
            </Box>
            <Box ref={layout.liveRef} role="status" aria-live="polite" sx={sr} />
            {layout.toast && (
                <Box role="status" sx={{ position: 'fixed', bottom: 28, left: 28, zIndex: 1400, bgcolor: 'text.primary', color: 'background.default', px: '18px', py: '13px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, boxShadow: shadowLg, animation: `${fadein} .3s ease` }}>
                    {layout.toast}
                </Box>
            )}
        </>
    );
}
